import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { ReactComponent as DeleteIcon } from '../../../../assets/icons/delete-icon.svg';
import { ReactComponent as EditIcon } from '../../../../assets/icons/edit.svg';
import { ReactComponent as LocationIcon } from '../../../../assets/icons/location-icon.svg';
import { ReactComponent as PlusIcon } from '../../../../assets/icons/plus.svg';
import { ReactComponent as StationIcon } from '../../../../assets/icons/sidebar/sidebar-account.svg';
import { ReactComponent as ExpandIcon } from '../../../../assets/icons/tables/expand-icon.svg';
import { ReactComponent as NewTabIcon } from '../../../../assets/icons/tables/new-tab.svg';
import DefaultApiLimit from '../../../../config/defaultLimit';
import { PortalType } from '../../../../config/enum';
import { usePortal } from '../../../../contexts/PortalContext/PortalContext';
import {
  AvailableStation,
  RequestStationInterface,
} from '../../../../interface/partner-interfaces/station-management.interface';
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks';
import { setNotificationMessage } from '../../../../redux/slices/NotificationManagementSlice';
import { setPageTitle } from '../../../../redux/slices/PageTitleSlice';
import {
  disableFiltersApplied,
  fetchApprovedStations,
  fetchListFilters,
  setApprovedStationFilterData,
  setSortData,
  updateStationFilters,
  refreshApprovedStations,
} from '../../../../redux/slices/Partners/ApprovedStationManagementSlice';
import { RootState } from '../../../../redux/store';
import MyButton from '../../../../shared-components/button/Button';
import CustomTable from '../../../../shared-components/custom-table/CustomTable';
import ModalBox from '../../../../shared-components/modal-box/ModalBox';
import DeviceSidePanel from '../../../../shared-components/table-side-panel/DeviceSidePanel';
import FacilityDetailsSidePanel from '../../../../shared-components/table-side-panel/FacilityDetailsSidePanel';
import { useTableSidePanel } from '../../../../shared-components/table-side-panel/hooks/useTableSidePanel';
import StationSidePanel from '../../../../shared-components/table-side-panel/StationSidePanel';
import { GetParametersFromCommonFilters } from '../../../../shared-functions/CommonFilters';
import ConvertUTCtoDateTime from '../../../../shared-functions/ConvertUTCtoDateTime';
import { removeEmptyKeys } from '../../../../shared-functions/DataTime';
import { exportCSV, exportPDF } from '../../../../shared-functions/ExportFile';
import FormatUtcString from '../../../../shared-functions/FormatUtcString';
import { permissionCheck } from '../../../../shared-functions/JWTDocodeFunction';
import stationApi from '../../../../utils/api/partner/station';

import AddStation from './AddStation';

interface ModelBoxDetailsInterface {
  title: string;
  header: boolean;
  status: boolean;
  content: React.JSX.Element;
}

interface ApprovedListProperties {
  renderAvailability: (value: AvailableStation) => JSX.Element;
  preventNavigation?: boolean;
  fetchByPartner?: string;
  updateCounts?: () => void;
}

/**
 * Add new station component
 * @param {any} param0 - The param0
 * @param {any} param0.renderAvailability - The renderAvailability prop
 * @param {any} param0.preventNavigation - The preventNavigation prop
 * @param {any} param0.fetchByPartner - Account custom ID to filter stations by, when scoped to a single partner
 */
function ApprovedList({
  renderAvailability,
  preventNavigation = false,
  fetchByPartner,
  updateCounts,
}: Readonly<ApprovedListProperties>) {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation('station');
  const isPartnerPortal = localStorage.getItem('activePortal') === 'partner';
  const [exportLoader, setExportLoader] = useState<boolean>(false);
  const [hiddenColumns, setHiddenColumns] = useState<string[]>([]);
  const {
    stations,
    page,
    limit,
    loading,
    totalPages,
    totalResults,
    filters,
    filterListLoading,
    isFilterUpdated,
    sortBy,
    sortField,
  } = useAppSelector((state: RootState) => state.partnersApprovedStation);
  const { currentFacilityTagsList } = useAppSelector((state) => state.partnersFacilityTags);
  const [searchParameters, setSearchParameters] = useSearchParams();

  /**
   * Navigates to the station dashboard.
   * @param {any} stationId The station custom ID
   */
  const handleExpandStation = (stationId: string | any) => {
    const stationDetails = stations.find((value: AvailableStation) => value._id === stationId);
    if (!stationDetails) return;
    navigate(
      `/ev-admin/station-management/${stationDetails._id}/station-dashboard?searchBy=${stationDetails.name}`,
      { state: { source: window.location.pathname + window.location.search } },
    );
  };

  /**
   * Opens the station dashboard in a new tab.
   * @param {any} stationId The station custom ID
   */
  const handleOpenStationInNewTab = (stationId: string | any) => {
    const stationDetails = stations.find((value: AvailableStation) => value._id === stationId);
    if (!stationDetails) return;
    window.open(
      `/ev-admin/station-expanded/${stationDetails._id}/station-dashboard?searchBy=${stationDetails.name}&source=${window.location.pathname}${window.location.search}`,
      '_blank',
    );
  };
  // const [currentRowData, setCurrentRowData] = useState<AvailableStation | null>(null);
  const [sidePanelOpen, setSidePanelOpen] = useState<boolean>(false);
  const [modalBoxDetail, setModalBoxDetail] = useState<ModelBoxDetailsInterface>({
    title: '',
    header: false,
    status: false,
    content: <div />,
  });

  // const [selectedStationId, setSelectedStationId] = useState<string | null>(null);

  const { page: pageParameter } = useParams<{ page: string }>();
  const { portalType } = usePortal();

  /**
   * Handle the fetch the data function
   * @param {any} data - The data
   */
  const fetchData = (data: RequestStationInterface) => {
    const selectedFacility = currentFacilityTagsList.find((item) => item.isSelected); // temporarily
    console.log(selectedFacility?._id, portalType, PortalType);

    if (!loading) {
      dispatch(
        fetchApprovedStations({
          ...data,
          ...(fetchByPartner ? { accountCustomIds: [fetchByPartner] } : {}),
        }),
      );
    }
  };

  /**
   * Handle the filter data clear function
   * @param {any} sortData - The sortData
   */
  const handleSortFunction = (sortData: any) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    dispatch(
      setApprovedStationFilterData({
        ...sortData,
      }),
    );
    dispatch(setSortData(sortData));
    fetchData({
      ...queryParameters,
      sortBy: sortData.sortBy,
      sortField: sortData.sortField,
      page: DefaultApiLimit.page,
      limit,
    });
  };
  // /**
  //  * Function to handle page change
  //  * @param pageNumber
  //  */
  // const handlePageChange = (pageNumber: number) => {
  //   const queryParameters = GetParametersFromCommonFilters(filters);
  //   fetchData({
  //     ...queryParameters,
  //     sortField,
  //     sortBy,
  //     page: pageNumber,
  //     limit,
  //   });
  // };

  /**
   * Function to handle data limit change
   * @param {any} value - The value
   */
  const handleLimitChange = (value: any) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    fetchData({
      ...queryParameters,
      page: DefaultApiLimit.page,
      ...value,
      sortBy,
      sortField,
    });
  };
  /**
   * Auto refresh the table data
   */
  const handleAutoRefresh = () => {
    const queryParameters = GetParametersFromCommonFilters(filters);

    dispatch(
      refreshApprovedStations({
        ...queryParameters,
        page,
        limit,
        sortBy,
        sortField,
        ...(fetchByPartner ? { accountCustomIds: [fetchByPartner] } : {}),
      }),
    );
  };
  /**
   * Current columns for export
   */
  const currentColumns = [
    { label: t('sNo'), accessorKey: '__serial__' },
    { label: t('stationName'), accessorKey: 'name' },
    {
      label: t('facilityName'),
      accessorKey: 'accountName',
    },
    {
      label: t('facilityAccountId'),
      accessorKey: 'accountCustomId',
    },
    { label: t('Map Location'), accessorKey: 'address' },
    { label: t('chargerOnline'), accessorKey: 'onlineChargersCount' },
    { label: t('chargerOffline'), accessorKey: 'offlineChargersCount' },
    { label: t('publiclyAccessible'), accessorKey: 'isAvailable' },
    { label: t('Created At'), accessorKey: 'createdAt' },
    { label: t('Updated At'), accessorKey: 'updatedAt' },
  ];

  /**
   * Filtering columns for export without hidden columns
   */
  const exportCurrentColumns = currentColumns.filter(
    (col) => col.accessorKey === '__serial__' || !hiddenColumns.includes(col.accessorKey),
  );

  /**
   * Export data function
   * @param {any} type - The type
   */
  const exportDataPdfOrCSV = (type: string) => {
    setExportLoader(true);
    const queryParameters = GetParametersFromCommonFilters(filters);
    stationApi
      .fetchStationsExport(
        removeEmptyKeys({
          ...queryParameters,
          submissionStatus: 'approved',
          sortBy,
          sortField,
          ...(fetchByPartner ? { accountCustomIds: [fetchByPartner] } : {}),
        }),
      )
      .then((response) => {
        // Convert data to CSV format
        const headers = exportCurrentColumns.map((col) => col.label);
        const rows = response.data.map((row: AvailableStation, index: number) =>
          exportCurrentColumns.map((col) => {
            switch (col.accessorKey) {
              case '__serial__':
                return index + 1;
              case 'opensAt':
                return `${FormatUtcString(row.opensAt)} - ${FormatUtcString(row.closesAt)}`;
              case 'isAvailable':
                return row.isAvailable ? t('available') : t('unavailable');
              case 'createdAt':
                return ConvertUTCtoDateTime(row.createdAt || '');
              case 'updatedAt':
                return ConvertUTCtoDateTime(row.updatedAt || '');
              default:
                return (row as any)[col?.accessorKey] || 'N/A';
            }
          }),
        );
        setExportLoader(false);
        if (rows.length > 0) {
          if (type === 'csv') {
            exportCSV(headers, rows, 'approved-station-list.csv');
          } else {
            exportPDF(headers, rows, 'approved-station-list.pdf');
          }
        }
      })
      .catch((error) => {
        setExportLoader(false);
        dispatch(
          setNotificationMessage({
            status: true,
            type: 'error',
            message:
              error?.response?.data?.message ||
              error?.message ||
              t('somethingWentWrong', 'Something went wrong!'),
            code: error?.response?.data?.code || error?.response?.status || 0,
          }),
        );
      });
  };

  /**
   * Function for closing model
   */
  const handleCloseModel = () => {
    setModalBoxDetail((previous) => ({
      ...previous,
      status: false,
    }));
  };

  /**
   * Function to refresh data
   */
  const handleRefresh = () => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    fetchData({ ...queryParameters, page, limit, sortBy, sortField });
    if (updateCounts) {
      updateCounts();
    }
  };

  /**
   * Add account function
   */
  const addStation = () => {
    const parameters = new URLSearchParams(searchParameters);
    parameters.delete('stationId'); // close row panel if open
    parameters.delete('accountCustomId'); // close row panel if open
    setSearchParameters(parameters);

    setSidePanelOpen(true);
  };

  const {
    isEditMode,
    openStatus,
    setOpenStatus,
    modalView,
    handleEditModeChange,
    handleSidePanelClose,
    renderSidePanelTopLeftContent,
    handleDeleteStationClick,
  } = useTableSidePanel({
    onRefresh: handleRefresh,
    sessions: stations,
  });

  /**
   * Handle the open side panel on row click function
   * @param {any} _ - row click parameter
   * @param {any} row - The facility tag data
   */
  const handleOpenSidePanelOnRowClick = (_: string, row: AvailableStation) => {
    if (!row?._id) return;

    handleEditModeChange(false);

    const parameters = new URLSearchParams(searchParameters);
    parameters.delete('accountCustomId');
    parameters.set('stationId', row._id);
    setSearchParameters(parameters);

    setSidePanelOpen(false);
    // setCurrentRowData(row);
  };

  const column = [
    {
      label: t('stationName'),
      accessorKey: 'name' as keyof AvailableStation,
      sortAllow: true,
      /**
       * No of stations
       * @param {any} currentRow - The currentRow
       */
      customData: (currentRow: AvailableStation) => (
        <p className="hyper-link-coloum">{currentRow.name || '-'}</p>
      ),
    },
    ...(!isPartnerPortal
      ? [
          {
            label: t('facilityName'),
            accessorKey: 'accountName' as keyof AvailableStation,
            sortAllow: true,
            /**
             * Facility name view in table
             * @param row
             * @returns
             */
          },
          {
            label: t('facilityAccountId'),
            accessorKey: 'accountCustomId' as keyof AvailableStation,
            sortAllow: true,
            queryParamsKey: 'accountCustomId',
            queryParamsValue: 'accountCustomId',
            sidePanelComponent: (
              <FacilityDetailsSidePanel
                actionActive
                isEditMode={isEditMode}
                onEditModeChange={handleEditModeChange}
                fetchIdFrom="queryParams"
                onSuccess={() => {
                  handleEditModeChange(false);
                  handleRefresh();
                }}
                onRefreshList={handleRefresh}
              />
            ),
            disableNewTab: true,
          },
        ]
      : []),
    {
      label: t('Address'),
      accessorKey: 'address' as keyof AvailableStation,
      sortAllow: false,
      columnWidth: 200,
      /**
       * Location view in table
       * @param {any} station - The station
       */
      customData: (station: AvailableStation) =>
        station.address ? (
          <div className="location-in-station-table">
            <span>
              <LocationIcon />
            </span>
            <p className="table-row-each-data">{station.address}</p>
          </div>
        ) : null,
    },
    {
      label: t('chargerOnline'),
      accessorKey: 'onlineChargersCount' as keyof AvailableStation,
      columnWidth: 120,
      /**
       * Custom Data for online chargers count
       * @param {any} station - The station
       */
      customData: (station: AvailableStation) => <div>{station.onlineChargersCount || '0'}</div>,
    },
    {
      label: t('chargerOffline'),
      accessorKey: 'offlineChargersCount' as keyof AvailableStation,
      columnWidth: 110,
      /**
       * Custom Data for online chargers count
       * @param {any} station - The station
       */
      customData: (station: AvailableStation) => <div>{station.offlineChargersCount || '0'}</div>,
    },
    {
      label: t('publiclyAccessible'),
      accessorKey: 'isAvailable' as keyof AvailableStation,
      customData: renderAvailability,
    },
    {
      label: t('actions'),
      accessorKey: '_id' as keyof AvailableStation,
      columnWidth: 160,
      sortAllow: false,
      hideWhenSidePanelOpen: true,
      sticky: 'right' as const,
      /**
       * Data UI in table
       * @param {any} station - The station data
       */
      customData: (station: AvailableStation) => (
        <div className="action-buttons-wrap">
          {station?._id && (
            <>
              <div
                role="presentation"
                className="action-button"
                onClick={(event) => {
                  event.stopPropagation();
                  handleExpandStation(station._id);
                }}>
                <ExpandIcon className="icon expand" />
              </div>
              <div
                role="presentation"
                className="action-button"
                onClick={(event) => {
                  event.stopPropagation();
                  handleOpenStationInNewTab(station._id);
                }}>
                <NewTabIcon className="icon new-tab" />
              </div>
              <div
                role="presentation"
                className={`action-button ${
                  !permissionCheck('partnerChargingStationUpdate') &&
                  !permissionCheck('cpoChargingStationUpdate')
                    ? 'disable-action'
                    : ''
                }`}
                onClick={(event) => {
                  event.stopPropagation();

                  if (
                    !permissionCheck('partnerChargingStationUpdate') &&
                    !permissionCheck('cpoChargingStationUpdate')
                  ) {
                    return;
                  }
                  handleEditModeChange(true);
                  const parameters = new URLSearchParams(searchParameters);
                  parameters.delete('accountCustomId');
                  parameters.set('stationId', station._id as string);
                  parameters.set('action', 'edit');
                  setSearchParameters(parameters);
                  setSidePanelOpen(false);
                  // setCurrentRowData(station);
                }}>
                <EditIcon className="icon edit" />
              </div>
              <div
                role="presentation"
                className={`action-button ${
                  !permissionCheck('partnerChargingStationDelete') &&
                  !permissionCheck('cpoChargingStationDelete')
                    ? 'disable-action'
                    : ''
                }`}
                onClick={(event) => {
                  event.stopPropagation();
                  if (
                    !permissionCheck('partnerChargingStationDelete') &&
                    !permissionCheck('cpoChargingStationDelete')
                  ) {
                    return;
                  }
                  handleDeleteStationClick(station._id);
                }}>
                <DeleteIcon className="icon delete" />
              </div>
            </>
          )}
        </div>
      ),
    },
  ];

  const previousFetchByPartner = useRef<string | undefined>();

  useEffect(() => {
    const hasPartnerChanged = previousFetchByPartner.current !== fetchByPartner;

    if (isFilterUpdated || stations.length === 0 || hasPartnerChanged) {
      const queryParameters = GetParametersFromCommonFilters(filters);

      fetchData({
        ...queryParameters,
        page: DefaultApiLimit.page,
        limit: DefaultApiLimit.limit,
      });

      dispatch(disableFiltersApplied());
      previousFetchByPartner.current = fetchByPartner;
    }
  }, [currentFacilityTagsList, filters, fetchByPartner]);

  useEffect(() => {
    if (preventNavigation) {
      return;
    }
    const hasSidePanelParameter = searchParameters.get('stationId');

    if (!hasSidePanelParameter) {
      dispatch(
        setPageTitle({
          title: t('chargingStations'),
          icon: <StationIcon />,
          count: totalResults,
        }),
      );
    } else {
      dispatch(
        setPageTitle({
          title: t('chargingStations'),
          icon: <StationIcon />,
          count: undefined,
        }),
      );
    }
  }, [totalResults, searchParameters, t, preventNavigation]);

  useEffect(() => {
    if (pageParameter === 'add') {
      setSidePanelOpen(true);
    }
  }, [pageParameter]);

  useEffect(() => {
    if (
      searchParameters.get('stationId') ||
      searchParameters.get('deviceId') ||
      (!window.location.pathname.includes('payout-management') &&
        searchParameters.get('accountCustomId'))
    ) {
      const parameters = new URLSearchParams(searchParameters);
      parameters.delete('stationId');
      parameters.delete('deviceId');
      if (!window.location.pathname.includes('payout-management')) {
        parameters.delete('accountCustomId');
      }
      setSearchParameters(parameters, { replace: true });
    }
  }, []);
  return (
    <>
      <ModalBox
        title={modalBoxDetail?.title}
        header={modalBoxDetail?.header}
        open={modalBoxDetail?.status}
        closeModal={handleCloseModel}
        content={modalBoxDetail?.content}
      />
      <ModalBox
        title={modalView.title}
        header={modalView.header}
        open={openStatus}
        closeModal={() => setOpenStatus(false)}
        content={modalView.content}
      />
      <div className="approved-list-table-container">
        <CustomTable
          page={page}
          dataKey="StationApproved"
          loading={loading}
          limit={limit}
          header={column}
          data={stations}
          height={165}
          sortData={{
            sortBy,
            sortField,
          }}
          handleSortChange={handleSortFunction}
          totalPages={totalPages}
          totalResults={totalResults}
          // handlePageChange={handlePageChange}
          pageLimitChange={handleLimitChange}
          exportDataPdfOrCSV={
            permissionCheck('partnerChargingStationExport') ||
            permissionCheck('cpoChargingStationExport')
              ? exportDataPdfOrCSV
              : undefined
          }
          exportLoader={exportLoader}
          exportData={stations}
          filters={filters}
          filtersToDispatch={updateStationFilters}
          filterListLoading={filterListLoading}
          filterListToDispatch={fetchListFilters}
          refreshRequired
          refershCurrentPages={handleRefresh}
          hoverLabel={t('refresh')}
          serialNo
          created
          lastUpdated
          onHiddenColumnsChange={setHiddenColumns}
          rowClickEvent={handleOpenSidePanelOnRowClick}
          rowClickParamsKey={searchParameters.get('deviceId') ? 'deviceId' : 'stationId'}
          rowClickSidePanel={
            searchParameters.get('deviceId') ? (
              <DeviceSidePanel
                fetchIdFrom="queryParams"
                isEditMode={isEditMode}
                onEditModeChange={handleEditModeChange}
                onSuccess={() => {
                  handleEditModeChange(false);
                  handleRefresh();
                }}
              />
            ) : (
              <StationSidePanel
                fetchIdFrom="queryParams"
                initialIsEditMode={isEditMode}
                onEditModeChange={handleEditModeChange}
                onDeleteStation={handleDeleteStationClick}
                onSuccess={() => {
                  handleEditModeChange(false);
                  handleRefresh();
                }}
              />
            )
          }
          sidePanelEnabled={sidePanelOpen}
          sidePanelManualContent={
            <AddStation
              onClose={() => setSidePanelOpen(false)}
              onSuccess={handleRefresh}
              fetchByPartner={fetchByPartner}
            />
          }
          explore={false}
          onSidePanelClose={() => {
            handleSidePanelClose();
            setSidePanelOpen(false);
          }}
          sidePanelTopLeftContent={
            sidePanelOpen ? (
              <p className="side-panel-header-title">{t('addStation')}</p>
            ) : (
              (renderSidePanelTopLeftContent() ?? undefined)
            )
          }
          rightTopContent={
            <div className="table-bottom-button-list">
              <div className="table-button-item">
                <MyButton
                  label={t('addStation')}
                  buttonType="submit"
                  onClickFunc={addStation}
                  svgIcon={<PlusIcon />}
                  disableButton={
                    !permissionCheck('partnerChargingStationAdd') &&
                    !permissionCheck('cpoChargingStationAdd')
                  }
                />
              </div>
            </div>
          }
          requiredRefreshToggle
          refreshFunction={handleAutoRefresh}
        />
      </div>
    </>
  );
}

ApprovedList.defaultProps = {
  preventNavigation: false,
};

export default ApprovedList;
